import { approveLeave, rejectLeave, processCancellationApproval, approveShortLeave, rejectShortLeave, approveEncashment, rejectEncashment } from '../../api/Attendance/leaves';
import { approveShiftSwap, rejectShiftSwap, acceptShiftSwap, declineShiftSwap } from '../../api/Attendance/shiftSwap';
import { decideCorrectionRequest } from '../../api/Attendance/correctionRequests';
import { decideOutOfBoundsApproval, type OutOfBoundsClockType } from '../../api/Attendance/outOfBoundsApproval';
import { editUserClaim } from '../../api/Payroll/claim';
import { UserClaimStatus } from '../../models/payroll';
import { reviewAssetRequest, AssetRequestStatus } from '../../api/General/assets';
import { acceptTerms } from '../../api/General/termsAndPolicies';
import { acceptAssignment, declineAssignment } from '../../api/TournamentManagement/refereeApi';
import type { PendingActionItem, PendingActionSource } from '../../api/pendingActions';

// Per-source action dispatch for the Pending Actions inbox. The backend enforces
// authorization; this layer only maps (item, decision) → the domain endpoint.
// `positive` = Approve / Accept / Complete; `!positive` = Reject / Decline.

export interface DispatchResult {
  errors: { code?: string; message?: string; description?: string }[];
}

/** Sources whose NEGATIVE decision requires a non-empty note (domain rules). */
const COMMENT_REQUIRED_ON_REJECT: PendingActionSource[] = ['Leave', 'Claim', 'AssetRequest', 'OutOfBoundsClock'];

export function requiresComment(item: PendingActionItem, positive: boolean): boolean {
  return !positive && COMMENT_REQUIRED_ON_REJECT.includes(item.source);
}

/**
 * Whether the confirmation step can carry a note. Shift-swap Accept/Decline
 * endpoints take no body, and Terms acceptance is a bare POST — showing a
 * note field there would silently drop the user's text.
 */
export function supportsNote(item: PendingActionItem): boolean {
  return item.source !== 'ShiftSwapAcceptance' && item.source !== 'Terms';
}

export interface RouteAccessContext {
  role: string;
  isManager: boolean;
}

/**
 * Web deep-link per source. `allowedRoles` mirrors the App.tsx route guard for
 * that path (RoleProtectedRoute semantics: primary role + synthetic 'Manager'
 * when the user has direct reports) — a link the guard would bounce to '/' is
 * worse than no link. Sources without an entry have no richer page (detail
 * dialog only).
 */
const SOURCE_ROUTE_DEFS: Partial<Record<PendingActionSource, { path: string; allowedRoles?: string[] }>> = {
  Leave: { path: '/leave-approvals', allowedRoles: ['Manager', 'Hr', 'Chief'] },
  ShiftSwap: { path: '/shift-swaps' },
  ShiftSwapAcceptance: { path: '/shift-swaps' },
  Claim: { path: '/payroll/claims', allowedRoles: ['Hr'] },
  AttendanceCorrection: { path: '/attendance/correction-requests' },
  Onboarding: { path: '/onboarding/complete' },
  Terms: { path: '/terms-and-policies/accept' },
  Survey: { path: '/employee-impact/enps' },
  ReturnToWork: { path: '/return-to-work' },
};

/** The deep-link for a source, or undefined when none exists or the caller's role can't open it. */
export function getSourceRoute(source: PendingActionSource, ctx: RouteAccessContext): string | undefined {
  const def = SOURCE_ROUTE_DEFS[source];
  if (!def) return undefined;
  if (!def.allowedRoles) return def.path;
  const effectiveRoles = new Set([ctx.role]);
  if (ctx.isManager) effectiveRoles.add('Manager');
  return def.allowedRoles.some((r) => effectiveRoles.has(r)) ? def.path : undefined;
}

export async function dispatchDecision(
  item: PendingActionItem,
  positive: boolean,
  note: string,
): Promise<DispatchResult> {
  const numericId = Number.parseInt(item.id, 10);
  switch (item.source) {
    case 'Leave': {
      // Cancellation requests approve/reject through their own endpoint.
      if (item.meta?.action === 'cancellation') {
        return processCancellationApproval(numericId, positive, note || undefined);
      }
      return positive ? approveLeave(numericId, note || undefined) : rejectLeave(numericId, note);
    }
    case 'ShiftSwap':
      return positive
        ? approveShiftSwap(numericId, { comments: note || null })
        : rejectShiftSwap(numericId, { comments: note || null });
    case 'ShiftSwapAcceptance':
      return positive ? acceptShiftSwap(numericId) : declineShiftSwap(numericId);
    case 'Claim':
      return editUserClaim(numericId, {
        id: numericId,
        status: positive ? UserClaimStatus.Approved : UserClaimStatus.Rejected,
        managerComment: note || undefined,
      });
    case 'AttendanceCorrection':
      return decideCorrectionRequest({
        requestId: Number.parseInt(item.meta?.requestId ?? item.id, 10),
        isApproved: positive,
        comment: note || undefined,
      });
    case 'OutOfBoundsClock':
      return decideOutOfBoundsApproval({
        shiftId: Number.parseInt(item.meta?.shiftId ?? item.id, 10),
        clockType: (item.meta?.clockType as OutOfBoundsClockType) ?? 'ClockIn',
        isApproved: positive,
        comment: note || undefined,
      });
    case 'ShortLeave':
      return positive
        ? approveShortLeave(numericId, { comments: note || undefined })
        : rejectShortLeave(numericId, { comments: note || undefined });
    case 'Encashment':
      return positive
        ? approveEncashment(numericId, { comments: note || undefined })
        : rejectEncashment(numericId, { comments: note || undefined });
    case 'AssetRequest':
      return reviewAssetRequest({
        requestId: numericId,
        status: positive ? AssetRequestStatus.Approved : AssetRequestStatus.Rejected,
        comment: note || undefined,
      });
    case 'Terms':
      return acceptTerms();
    case 'Referee': {
      const matchId = Number.parseInt(item.meta?.matchId ?? item.id, 10);
      return positive
        ? acceptAssignment({ matchId, reason: note || undefined })
        : declineAssignment({ matchId, reason: note || undefined });
    }
    default:
      return Promise.resolve({ errors: [{ description: `Unsupported action source: ${item.source}` }] });
  }
}
